import authService from '@/services/auth';

const US_STATE_NAME_TO_CODE = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'Washington D.C.': 'DC',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL',
  Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA',
  Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
  Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
  'American Samoa': 'AS', Guam: 'GU', 'Northern Mariana Islands': 'MP',
  'Puerto Rico': 'PR', 'U.S. Virgin Islands': 'VI', 'US Virgin Islands': 'VI',
};

export function normalizeOrgStateCode(org) {
  if (!org) return '';
  const raw = String(org.primary_state || org.state || '').trim();
  if (!raw) return '';
  if (raw.length === 2) return raw.toUpperCase();
  return US_STATE_NAME_TO_CODE[raw] || '';
}

export function getStoredHubState() {
  return (localStorage.getItem('iqhub_selected_state') || '').trim().toUpperCase();
}

/**
 * Orgs where the user freely picks Policy IQ state (any of 50 for Demo;
 * listed jurisdictions for other multi-state orgs). Never auto-force org.state.
 */
export function getUserDefaultStates() {
  const user = authService.getUser();
  if (!user || !Array.isArray(user.default_states)) return [];
  return user.default_states
    .map((c) => String(c || '').trim().toUpperCase())
    .filter(Boolean);
}

export function isFreeStatePickOrg(org) {
  if (!org) return false;
  if (org.slug === 'demo') return true;
  if (getUserDefaultStates().length > 1) return true;
  return getOrgJurisdictions(org).length > 1;
}

/**
 * Resolve IQ Hub / Policy IQ selectedState.
 * User pick in localStorage always wins. Free-pick orgs (Demo, multi-jurisdiction)
 * never auto-select org.state. Single-state orgs still default to that state when
 * nothing is picked yet. Platform admin at home with no pick → National (blank).
 */
export function resolveHubSelectedState(isPlatformAdmin) {
  const stored = getStoredHubState();
  if (stored) return stored;

  const org = authService.getActiveOrganization();
  if (isFreeStatePickOrg(org)) {
    return '';
  }

  const userStates = getUserDefaultStates();
  if (userStates.length === 1 && !isPlatformAdmin) {
    return userStates[0];
  }

  const orgState = normalizeOrgStateCode(org);
  if (!isPlatformAdmin) {
    return orgState;
  }
  const homeOrg = authService.getUserOrganization();
  if (org && homeOrg && org.id !== homeOrg.id) {
    return orgState;
  }
  return '';
}

export function getOrgJurisdictions(org) {
  if (!org || !Array.isArray(org.primary_jurisdictions)) return [];
  return org.primary_jurisdictions
    .map((c) => String(c || '').trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Resolve the list of state codes the active identity can pick from in IQ Hub.
 * Empty array = unbounded (platform admin at home, or AccessIQ DEMO — full 50-state browsing).
 */
export function resolveActiveJurisdictions(isPlatformAdmin) {
  const org = authService.getActiveOrganization();
  if (org?.slug === 'demo') {
    return [];
  }
  const homeOrg = authService.getUserOrganization();
  const viewingAsClient = !!(org && homeOrg && org.id !== homeOrg.id);
  if (isPlatformAdmin && !viewingAsClient) {
    return [];
  }
  if (isPlatformAdmin && viewingAsClient) {
    return getOrgJurisdictions(org);
  }
  const userStates = getUserDefaultStates();
  if (userStates.length) {
    return userStates;
  }
  return getOrgJurisdictions(org);
}

export function initialMapModeForState(stateCode) {
  if (stateCode === 'FL') return 'ahca';
  if (stateCode === 'MI') return 'choice';
  if (stateCode === 'IN') return 'prep';
  if (stateCode === 'OH') return 'ochch';
  if (stateCode === 'UT') return 'lhd';
  if (stateCode === 'PA') return 'chc';
  if (stateCode === 'MN') return 'default';
  return 'region';
}

/** Platform admin at home — National scope, no state brief until explicitly chosen. */
export function clearPolicyContextStateForNational() {
  localStorage.removeItem('iqhub_selected_state');
  localStorage.setItem('pollie_context_state', 'NATIONAL');
}

/**
 * Sync Policy IQ state when switching org context.
 * Free-pick orgs (Demo / multi-jurisdiction): do not overwrite the user's pick
 * with org.state (that was forcing Demo → CO).
 * Single-state orgs: set hub + Pollie to that org's state.
 */
export function syncPolicyContextStateFromOrg(org) {
  if (isFreeStatePickOrg(org)) {
    return;
  }
  const code = normalizeOrgStateCode(org);
  if (code) {
    localStorage.setItem('iqhub_selected_state', code);
    localStorage.setItem('pollie_context_state', code);
  } else {
    localStorage.removeItem('iqhub_selected_state');
  }
}

/** Sync Pollie + hub scope when the user picks a state in IQ Hub. */
export function syncPollieContextStateFromHubState(stateCode) {
  const code = (stateCode || '').trim().toUpperCase();
  if (code) {
    localStorage.setItem('iqhub_selected_state', code);
    localStorage.setItem('pollie_context_state', code);
  } else {
    localStorage.removeItem('iqhub_selected_state');
    localStorage.setItem('pollie_context_state', 'NATIONAL');
  }
  window.dispatchEvent(new CustomEvent('iqhub:state-changed', { detail: { state: code } }));
}

export const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'Washington D.C.' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'AS', name: 'American Samoa' }, { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' }, { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
];

export function usStatesSorted() {
  return [...US_STATES].sort((a, b) => a.name.localeCompare(b.name));
}

/** Full name for a state code, or the code if unknown. */
export function hubStateDisplayName(code) {
  const c = (code || '').trim().toUpperCase();
  if (!c) return '';
  const hit = US_STATES.find((s) => s.code === c);
  return hit ? hit.name : c;
}

/** KPI / scope line tied to the picker state (e.g. "Florida (FL)"). */
export function hubStateScopeLabel(code) {
  const c = (code || '').trim().toUpperCase();
  if (!c) return 'Pick a state';
  const name = hubStateDisplayName(c);
  if (!name || name === c) return c;
  return `${name} (${c})`;
}
